import { ethers } from "ethers"
import { withTimeout } from "@/lib/protection"

// Provider for Ethereum mainnet
const provider = new ethers.JsonRpcProvider(
  process.env.ETHEREUM_RPC_URL || 
  process.env.NEXT_PUBLIC_ETHEREUM_RPC_URL || 
  "https://eth-mainnet.g.alchemy.com/v2/demo"
)

/**
 * Error types for ENS resolution
 */
export enum EnsErrorType {
  INVALID_FORMAT = "INVALID_FORMAT",
  NOT_FOUND = "NOT_FOUND",
  RESOLUTION_ERROR = "RESOLUTION_ERROR",
  NONE = "NONE",
}

/**
 * Validates if a string is a properly formatted ENS name
 * ENS supports Unicode characters through NFKC normalization
 * @param name String to validate as ENS name
 * @returns Boolean indicating if the name is valid ENS format
 */
export function isValidEnsFormat(name: string): boolean {
  // ENS names must not contain spaces
  if (name.includes(" ")) return false

  // Must contain at least one dot
  if (!name.includes(".")) return false

  // Trim whitespace
  const trimmed = name.trim()
  if (!trimmed) return false

  // Split by dots to validate each label
  const labels = trimmed.split(".")
  
  // Must have at least 2 labels (e.g., "example.eth")
  if (labels.length < 2) return false

  // Each label must not be empty
  for (const label of labels) {
    if (!label || label.length === 0) return false
    
    // Labels cannot start or end with hyphen (after normalization)
    // But we allow Unicode characters - let the resolver handle validation
    // Just check for obviously invalid characters like control characters
    if (/[\x00-\x1F\x7F]/.test(label)) return false
  }

  // TLD (last label) should be at least 2 characters
  const tld = labels[labels.length - 1]
  if (tld.length < 2) return false

  // ENS supports Unicode, so we use a more permissive validation
  // The actual resolver will handle NFKC normalization and determine validity
  return true
}

/**
 * Resolves an ENS name to an Ethereum address
 * @param ensNameOrAddress ENS name (e.g., "vitalik.eth") or Ethereum address
 * @returns Object with resolved address and any error information
 */
export async function resolveEnsName(ensNameOrAddress: string): Promise<{
  address: string | null
  error: string | null
  errorType: EnsErrorType
}> {
  try {
    // If it's already a valid Ethereum address, return it
    if (ethers.isAddress(ensNameOrAddress)) {
      return { address: ensNameOrAddress, error: null, errorType: EnsErrorType.NONE }
    }

    // Check if it's a valid ENS name format
    if (!isValidEnsFormat(ensNameOrAddress)) {
      return {
        address: null,
        error: "Please enter a valid ENS name, ensuring it follows the correct format (e.g., example.eth).",
        errorType: EnsErrorType.INVALID_FORMAT,
      }
    }

    // Prefer server API in browser to avoid provider limits on Vercel
    if (typeof window !== 'undefined') {
      try {
        const res = await fetch(`/api/ens-resolve?name=${encodeURIComponent(ensNameOrAddress)}`)
        const json = await res.json()
        if (json?.success && json?.result?.address) {
          return { address: json.result.address, error: null, errorType: EnsErrorType.NONE }
        }
      } catch {}
    }

    // Fallback to direct provider
    const resolvePromise = provider.resolveName(ensNameOrAddress)
    const address = await withTimeout(resolvePromise, 10000)

    if (!address) {
      return {
        address: null,
        error: "ENS name not found. Please verify the spelling and ensure the name is correctly registered.",
        errorType: EnsErrorType.NOT_FOUND,
      }
    }

    return { address, error: null, errorType: EnsErrorType.NONE }
  } catch (error) {
    console.error("Error resolving ENS name:", error)

    // Check if this is a timeout error
    if (error.message && error.message.includes("timed out")) {
      return {
        address: null,
        error:
          "ENS resolution timed out. This may happen with certain ENS names. Please try again later or use the Ethereum address directly.",
        errorType: EnsErrorType.RESOLUTION_ERROR,
      }
    }

    return {
      address: null,
      error: "Error resolving ENS name. Please try again later.",
      errorType: EnsErrorType.RESOLUTION_ERROR,
    }
  }
}

/**
 * Checks if a string is likely an ENS name
 * @param input String to check
 * @returns Boolean indicating if the input is likely an ENS name
 */
export function isLikelyEnsName(input: string): boolean {
  // Simple check: contains a dot and ends with a TLD like .eth, .xyz, etc.
  // But no spaces allowed
  // Support Unicode characters in ENS names
  return !input.includes(" ") && input.includes(".") && /\.[a-z0-9]{2,}$/i.test(input)
}

/**
 * Looks up the ENS name for an Ethereum address
 * @param address Ethereum address
 * @returns ENS name or null if none exists
 */
export async function lookupEnsName(address: string): Promise<string | null> {
  try {
    if (!ethers.isAddress(address)) {
      return null
    }

    if (typeof window !== 'undefined') {
      try {
        const res = await fetch(`/api/ens-lookup?address=${encodeURIComponent(address)}`)
        const json = await res.json()
        if (json?.success) return json.name || null
      } catch {}
    }

    const ensName = await provider.lookupAddress(address)
    return ensName
  } catch (error) {
    console.error("Error looking up ENS name:", error)
    return null
  }
}
